import React, { useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { toast } from "react-toastify";

import { factoryUseModalX } from "@/lib/factoryUseModalX";
import fetchSingleton from "@/lib/fetchSingleton";
import { submitFunction, useFormValidation } from "@/lib/formValidationHelpers";
import { useAuth } from "@/lib/useAuth";
import { useData } from "@/lib/useData";
import { useEventPublisher } from "@/lib/useEventEmitter";
import { useProgramId } from "@/lib/useProgramId";
import PartFormError from "@/PartFormError";
import PartFormSaveButton from "@/PartFormSaveButton";
import PartLoader from "@/PartLoader";
import PartModal, { type PartModalRef } from "@/PartModal";
import formStyles from "@/styles/form.module.scss";
import { Community } from "@/types/api-types";
import { communityEditorSchemaJoi } from "@/validations";

type IFormInput = Community;

interface IFormInputRef {
  reset: () => void;
}

export const useModalCommunityEditor = factoryUseModalX({
  queryName: "modalCommunityEditor",
});

export const CommunityEditor = React.forwardRef<
  IFormInputRef,
  { setConfirmClose?: (arg0: boolean) => void }
>(({ setConfirmClose }, ref) => {
  const { isCreating, isEditing, editingId, finishInteraction } =
    useModalCommunityEditor();

  const { programId } = useProgramId();

  const formMethods = useForm<IFormInput>({
    reValidateMode: "onSubmit",
    defaultValues: {
      program_id: programId,
      is_direct: false,
    },
  });

  const {
    register,
    control,
    formState: { errors, isDirty },
    reset,
    setError,
    clearErrors,
  } = formMethods;

  React.useImperativeHandle(ref, () => ({
    reset: () => reset(),
  }));

  const { handleSubmit, isSubmitting } = useFormValidation<IFormInput>({
    control,
    joiSchema: communityEditorSchemaJoi,
    context: { isEditing, editingId },
    setError,
    clearErrors,
  });

  const [existingItem, existingItemLoading] = useData(
    isEditing ? process.env.NEXT_PUBLIC_API_URL + `/api/v1/community/${editingId}` : "",
  );

  const publisher = useEventPublisher();

  useEffect(() => {
    if (isEditing && existingItem) reset(existingItem);
    if (isCreating && programId !== undefined)
      reset({ program_id: programId, is_direct: false });
  }, [isCreating, isEditing, existingItem, programId]);

  const { idToken } = useAuth();

  const onSubmit: submitFunction<IFormInput> = async (data, handleApiResult) => {
    let url = process.env.NEXT_PUBLIC_API_URL + "/api/v1/community";
    if (isEditing) url += "/" + editingId;

    const result = await fetchSingleton(url, {
      method: (isCreating && "POST") || (isEditing && "PUT") || "GET",
      body: JSON.stringify(data),
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
    });

    handleApiResult(result);

    const msg = (() => {
      if (isCreating) return "Community added successfully";
      if (isEditing) return "Community updated successfully";
    })();
    toast.success(msg, { autoClose: 5000 });

    finishInteraction();

    if (isCreating) publisher("communityCreated", result.id);
  };

  React.useEffect(() => {
    if (setConfirmClose) setConfirmClose(isDirty);
  }, [isDirty]);

  return (
    <FormProvider {...formMethods}>
      <PartLoader block active={existingItemLoading}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          onReset={() => {
            finishInteraction();
          }}
          className={formStyles.form}
          autoComplete="off"
        >
          <div className={formStyles.formMultipleColumns}>
            <div>
              <label className={formStyles.formLabel} htmlFor="name">
                Community Name <sup>*</sup>
              </label>
              <input
                id="name"
                // placeholder="Community Name"
                className={formStyles.formInput}
                {...register("name")}
                aria-invalid={errors.name ? "true" : "false"}
              />
              <PartFormError name="name" />
            </div>
          </div>
          <div className={formStyles.formMultipleColumns}>
            <div>
              <label className={formStyles.formLabel} htmlFor="description">
                Description
              </label>
              <textarea
                id="description"
                className={formStyles.formTextarea}
                {...register("description")}
                aria-invalid={errors.description ? "true" : "false"}
              />
              <PartFormError name="description" />
            </div>
          </div>
          <div className={formStyles.formActionButtonsContainer}>
            <button
              type="reset"
              className={formStyles.buttonReset}
              data-test-element-type="button"
              data-test-action-type="reset"
            >
              Discard
            </button>
            <PartFormSaveButton isSubmitting={isSubmitting}>Save</PartFormSaveButton>
          </div>
        </form>
      </PartLoader>
    </FormProvider>
  );
});

CommunityEditor.displayName = "CommunityEditor";

const ModalCommunity = React.forwardRef<PartModalRef>((props, ref) => {
  const { isCreating, isEditing, finishInteraction } = useModalCommunityEditor();

  const title =
    (isCreating && "Add New Community") ||
    (isEditing && "Edit Community") ||
    "Unknown action";

  const [shouldConfirmClose, setConfirmClose] = React.useState<boolean>(false);

  const formRef = React.useRef<IFormInputRef>(null);

  return (
    <PartModal
      ref={ref}
      title={title}
      showed={isCreating || isEditing}
      onClose={() => finishInteraction()}
      afterClose={() => formRef.current?.reset()}
      confirmCloseQuestion={shouldConfirmClose}
    >
      <CommunityEditor setConfirmClose={setConfirmClose} ref={formRef} />
    </PartModal>
  );
});

ModalCommunity.displayName = "ModalCommunity";

export default ModalCommunity;
